import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";

import Cs from "@/Models/Cs";
import api from "../Api";
import {
  deriveCommentConfirmed,
  fetchCommentsByBookIds,
} from "../DeliveryComments";

// ⚠️ 서버 기록 기준 추정값입니다. 실제 값은 백엔드 DTO/enum과 대조해서 맞춰주세요.
export const REGISTRATION_TYPE = {
  CUSTOMER: "CUSTOMER", // 고객
  CORP: "CORP", // 화주사
  ADMIN: "ADMIN", // 회사(관제 자체 등록)
};

// ⚠️ 서버가 실제로 sort 파라미터를 이 3개 문자열로 받는지 확인 필요
// (useDeliveriesStore는 sortKey/sortType 조합이지만, CS는 서버 기록상
//  discrete 3종 모드라 단일 enum으로 가정했습니다)
export const QUERY_SORT = {
  CREATED_AT: "createdAt", // CS 등록일시순 (기본값)
  COMMENT_CREATED_AT: "commentCreatedAt", // 코멘트 작성일순
  UNCONFIRMED_COMMENT: "unconfirmedComment", // 미확인 코멘트순
};

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
  SET_SELECTED: "SET_SELECTED",
};

const zonedDateYesterday = dfSubDays(getDateByTimeZone(), 1);
const zonedDateToday = getDateByTimeZone();
const initDateFrom = formatDate(zonedDateYesterday);
const initDateTo = formatDate(zonedDateToday);

const INITIAL_QUERY = {
  createdAtFrom: initDateFrom,
  createdAtTo: initDateTo,
  bookId: "",
  receiverName: "",
  receiverMobile: "",
  checked: undefined, // undefined=전체, true=처리완료, false=처리대기
  deleted: false, // 취소여부. "취소" 서브메뉴에서만 true로 조회
  page: 1,
  pageSize: 20,
  sort: QUERY_SORT.CREATED_AT,
};

const INITIAL_STATE = {
  cses: [],
  totalCount: 0,
  pageCount: 0,
  query: INITIAL_QUERY,
  selectedCs: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        cses: action.cses,
        totalCount: action.totalCount,
        pageCount: action.pageCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    case CONSTANTS.SET_SELECTED:
      return {
        ...state,
        selectedCs: action.selectedCs,
      };
    default:
      return INITIAL_STATE;
  }
};

const compactQuery = (query = {}) => {
  const next = {};

  Object.keys(query).forEach((key) => {
    const value = query[key];

    if (value === undefined || value === null || value === "") {
      return;
    }

    next[key] = value;
  });

  return next;
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useCsesStore = () => {
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(
    async (queryOverride) => {
      const query = compactQuery(queryOverride || state.query);
      const { rows, totalCount } = await api.get("/cses", query);
      const cses = (rows || []).map((d) => new Cs(d));
      const commentsByBookId = await fetchCommentsByBookIds(
        cses.map((cs) => cs.renderBookId()),
      );

      cses.forEach((cs) => {
        const comments = commentsByBookId[cs.renderBookId()] || [];
        cs.applyComments(comments);

        const confirmed = deriveCommentConfirmed(comments);

        if (confirmed !== null) {
          cs.commentConfirmed = confirmed;
        }
      });

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        cses,
        totalCount: totalCount || 0,
        pageCount:
          Math.floor((totalCount || 0) / (query.pageSize || 20)) +
          ((totalCount || 0) % (query.pageSize || 20) ? 1 : 0),
      });
    },
    [dispatch, state.query],
  );

  const fetchByBookId = useCallback(async (bookId, query = {}) => {
    if (!bookId) {
      return [];
    }

    const { rows } = await api.get(
      "/cses",
      compactQuery({
        bookId,
        page: 1,
        pageSize: 100,
        sort: QUERY_SORT.CREATED_AT,
        ...query,
      }),
    );

    return (rows || []).map((d) => new Cs(d));
  }, []);

  const create = useCallback(async ({ bookId, text }) => {
    return await api.post("/cses", {
      bookId,
      text,
      registrationType: REGISTRATION_TYPE.CORP,
    });
  }, []);

  const update = useCallback(async (id, { text }) => {
    return await api.put(`/cses/${id}`, {
      text,
    });
  }, []);

  const cancel = useCallback(async (id) => {
    return await api.delete(`/cses/${id}`);
  }, []);

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({ type: CONSTANTS.SET_QUERY, query });
    },
    [dispatch],
  );

  const setSelectedCs = useCallback(
    (selectedCs) => {
      dispatch({ type: CONSTANTS.SET_SELECTED, selectedCs });
    },
    [dispatch],
  );

  return {
    state,
    fetchAll,
    fetchByBookId,
    create,
    update,
    cancel,
    setQuery,
    setSelectedCs,
  };
};
